// Discrete rotation steps from cursor keys and swipes.
// Each key press or swipe queues one quarter-turn around the screen X or Y axis.

export interface Step {
  axis: "x" | "y";
  dir: 1 | -1;
}

export interface Tap {
  x: number;
  y: number;
}

const SWIPE_THRESHOLD = 30; // CSS px of travel before a drag counts as a swipe
const MAX_QUEUE = 2; // buffer a little input during animation, but don't spiral

const KEY_STEPS: Record<string, Step> = {
  ArrowLeft: { axis: "y", dir: -1 },
  ArrowRight: { axis: "y", dir: 1 },
  ArrowUp: { axis: "x", dir: -1 },
  ArrowDown: { axis: "x", dir: 1 },
};

export class StepInput {
  onTap: ((tap: Tap) => void) | null = null;

  private queue: Step[] = [];
  private pointerActive = false;
  private startX = 0;
  private startY = 0;

  constructor(target: HTMLElement) {
    window.addEventListener("keydown", (e) => {
      const step = KEY_STEPS[e.key];
      if (!step) return;
      e.preventDefault();
      this.push(step);
    });

    target.addEventListener("pointerdown", (e) => {
      this.pointerActive = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      target.setPointerCapture(e.pointerId);
    });
    target.addEventListener("pointerup", (e) => {
      if (!this.pointerActive) return;
      this.pointerActive = false;
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_THRESHOLD) {
        this.onTap?.({ x: e.clientX, y: e.clientY });
        return;
      }
      if (Math.abs(dx) >= Math.abs(dy)) {
        this.push({ axis: "y", dir: dx > 0 ? 1 : -1 });
      } else {
        this.push({ axis: "x", dir: dy > 0 ? 1 : -1 });
      }
    });
    target.addEventListener("pointercancel", () => {
      this.pointerActive = false;
    });
    // The game owns every button on the canvas; no browser menu on it.
    target.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  next(): Step | null {
    return this.queue.shift() ?? null;
  }

  private push(step: Step): void {
    if (this.queue.length < MAX_QUEUE) this.queue.push(step);
  }
}
